import type { MosaicPath } from "react-mosaic-component";
import { Button } from "@/components/ui/button";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTabs } from "@/context/tabs/TabsContext";
import type { EventEmitter } from "@/shared/eventEmiters/EventEmitter";
import type { ViewId } from "@/shared/types/common";
import sprites from "/src/shared/assets/sprite.svg";
import { MosaicMenu } from "../mosaicMenu/MosaicMenu";
import { createControls } from "./ToolbarControlsItems";

interface ToolbarControlsProps {
	path: MosaicPath;
	id: string;
	onColorChange: (color: string | null) => void;
	passedColor?: string | null;
	eventEmitter: EventEmitter;
}

export const ToolbarControls = ({
	path,
	id,
	onColorChange,
	eventEmitter,
	passedColor,
}: ToolbarControlsProps) => {
	const { getTabLayout, setTabLayout, getTabWidgetsState, setTabWidgetsState } =
		useTabs();
	const layout = getTabLayout();
	const tabWidgetsState = getTabWidgetsState();
	const controls = createControls(layout, setTabLayout, path);
	const baseId = id.split("-")[0] as ViewId;

	const closeControl = controls.find((control) => control.key === "close");
	const otherControls = controls.filter((control) => control.key !== "close");

	return (
		<>
			{otherControls.map((control) => (
				<Tooltip key={control.key}>
					<TooltipTrigger asChild>
						<Button
							variant="outline"
							type="button"
							className="bg-transparent font-bold pt-1 px-2 rounded border-none h-7"
							onClick={control.onClick}
						>
							<svg
								aria-label="control-btn"
								role="img"
								fill="#fff"
								className="w-3 h-3"
							>
								<use xlinkHref={`${sprites}${control.imgSrc}`} />
							</svg>
						</Button>
					</TooltipTrigger>
					<TooltipContent>{control.label}</TooltipContent>
				</Tooltip>
			))}

			<Tooltip key="settings">
				<TooltipTrigger asChild>
					<div>
						<MosaicMenu
							onColorChange={onColorChange}
							baseId={baseId}
							tileId={id}
							passedColor={passedColor}
							eventEmitter={eventEmitter}
						/>
					</div>
				</TooltipTrigger>
				<TooltipContent>Settings</TooltipContent>
			</Tooltip>

			{closeControl && (
				<Tooltip key={closeControl.key}>
					<TooltipTrigger asChild>
						<Button
							variant="outline"
							type="button"
							className="bg-transparent font-bold pt-1 px-1 rounded border-none h-7"
							onClick={() => {
								closeControl.onClick();
								if (tabWidgetsState) {
									const { [id]: _, ...newState } = tabWidgetsState;
									setTabWidgetsState(newState);
								}
							}}
						>
							<svg
								aria-label="control-btn"
								role="img"
								stroke="#fff"
								className="w-5 h-5"
							>
								<use xlinkHref={`${sprites}${closeControl.imgSrc}`} />
							</svg>
						</Button>
					</TooltipTrigger>
					<TooltipContent>{closeControl.label}</TooltipContent>
				</Tooltip>
			)}
		</>
	);
};
